import { computed, inject, Injectable, Injector } from '@angular/core'
import { UserState, initialUserState } from './user.state'
import { UserSelectors } from './user.selectors'
import { UserUpdaters } from './user.updaters'
import { UserEffects } from './user.effects'
import { getStorageItem, removeStorageItem } from '../../common/utils/storage.utils'
import { isCorruptedCrudSessionUser, isStaleDoctorProfileSessionUser } from '../../common/utils/crud-response.util'
import { NotificationsWsService } from '../../services/notifications-ws.service'
import { BaseSingletonStore } from '../base-store/base.singleton-store'
import { environment } from '../../../environments/environment'
import { resolveLegacyRoleCodes } from '../../permissions/legacy/legacy-role.resolver'
import { resolvePortalSubdomain } from '../../common/utils/portal-subdomain.util'

@Injectable({ providedIn: 'root' })
export class UserStore extends BaseSingletonStore<UserState> {
  private injector = inject(Injector)
  private get notificationsWs() {
    return this.injector.get(NotificationsWsService)
  }
  protected override readonly initialState = initialUserState

  private get effects() {
    return this.injector.get(UserEffects)
  }

  constructor() {
    super(initialUserState, 'UserStore')
  }

  readonly isAuthenticated = computed(() => !!this.user())
  readonly doctorUserBaseId = this.selectSignal(UserSelectors.selectDoctorUserBaseId)
  readonly fullName = computed(() => {
    const u = this.user()
    return u ? `${u.firstName} ${u.lastName}`.trim() : ''
  })

  readonly user = this.selectSignal(UserSelectors.selectUser)
  readonly userIsAuthenticated = this.selectSignal(UserSelectors.selectIsAuthenticated)
  readonly token = this.selectSignal(UserSelectors.selectUserToken)
  readonly refreshToken = this.selectSignal(UserSelectors.selectRefreshToken)
  readonly csrfToken = this.selectSignal(UserSelectors.selectCsrfToken)
  readonly isLoading = this.selectSignal(UserSelectors.selectIsLoading)
  readonly error = this.selectSignal(UserSelectors.selectError)
  readonly mfaToken = this.selectSignal(UserSelectors.selectMfaToken)
  readonly passwordExpiredEmail = this.selectSignal(UserSelectors.selectPasswordExpiredEmail)

  getToken() {
    return this.token()
  }
  getRefreshToken() {
    return this.refreshToken()
  }
  getCsrfToken() {
    return this.csrfToken()
  }

  readonly setUser = this.updater((state, user: UserState['user']) => UserUpdaters.setUser(state, user))
  readonly setToken = this.updater((state, token: UserState['token']) => UserUpdaters.setToken(state, token))
  readonly setRefreshToken = this.updater((state, token: UserState['refreshToken']) =>
    UserUpdaters.setRefreshToken(state, token),
  )
  readonly setCsrfToken = this.updater((state, token: UserState['csrfToken']) =>
    UserUpdaters.setCsrfToken(state, token),
  )
  readonly setMfaChallenge = this.updater((state, payload: { mfaToken: string }) =>
    UserUpdaters.setMfaChallenge(state, payload.mfaToken),
  )

  readonly setPasswordExpiredChallenge = this.updater((state, email: string) =>
    UserUpdaters.setPasswordExpiredChallenge(state, email),
  )

  clearMfaChallenge(): void {
    this.setState((state) => UserUpdaters.clearMfaChallenge(state))
  }

  clearPasswordExpiredChallenge(): void {
    this.setState((state) => UserUpdaters.clearPasswordExpiredChallenge(state))
  }

  changeExpiredPassword(payload: { email: string; currentPassword: string; newPassword: string }) {
    this.effects.changeExpiredPassword(payload)
  }

  login(credentials: { username: string; password: string }) {
    this.effects.login(credentials)
  }
  verifyMfa(payload: { mfaToken: string; code: string }) {
    this.effects.verifyMfa(payload)
  }
  logout() {
    this.effects.logout()
  }
  updateProfile(profile: any) {
    this.effects.updateProfile(profile)
  }
  getProfile() {
    this.effects.getProfile()
  }

  restoreSessionFromStorage(): void {
    const token = getStorageItem<string>('auth_token', false)
    const refresh = getStorageItem<string>('auth_refresh', false)
    const csrf = getStorageItem<string>('auth_csrf', false)
    const userStr = getStorageItem<string>('auth_user', false)

    if (token && userStr) {
      try {
        const parsedUser = JSON.parse(userStr)
        this.setToken(token)
        if (refresh) this.setRefreshToken(refresh)
        if (csrf) this.setCsrfToken(csrf)
        this.connectWs(token)

        if (isCorruptedCrudSessionUser(parsedUser)) {
          removeStorageItem('auth_user')
          this.getProfile()
          return
        }

        this.setUser(parsedUser)

        if (isStaleDoctorProfileSessionUser(parsedUser)) {
          this.getProfile()
          return
        }

        if (environment.profileApiModel === 'legacy') {
          this.getProfile()
        } else if (!resolveLegacyRoleCodes(parsedUser).length) {
          this.getProfile()
        }
      } catch {
        this.clearAuthStorage()
      }
    }
  }

  private connectWs(token: string): void {
    this.notificationsWs.connect(token, resolvePortalSubdomain())
  }

  private clearAuthStorage() {
    removeStorageItem('auth_user')
    removeStorageItem('auth_token')
    removeStorageItem('auth_refresh')
    removeStorageItem('auth_csrf')
  }
}
